import Service from '@ember/service';
import { service } from '@ember/service';
import Utils from '../utils/utils';
import upperFirst from 'lodash.upperfirst';

/**
 * Service that depends on / combines
 * hashService and the data model.
 *
 * This service should only be used in the index route as a dependency
 */
export default class HashDataService extends Service {
    @service hashService;
    @service('initialData') initial;
    @service('data') model;
    @service('aditools-analytics') analytics;
    @service sigmaDeltaData;
    @service partData;

    initializeData() {
        // Get an object with defaults / selection data
        let metadata = this.initial.metadata,
            modelData = this.initial.modelData,
            hashValues = this.hashService.parsedLocationHash;

        // Check the url for any data.  If found, override the defaults
        Object.keys(hashValues).forEach((hashKey) => this._handleHashItem(hashKey, metadata, modelData));
        this._manualLoadFromHash(hashValues, modelData);

        // Set the data to the model
        this.model.setProperties(modelData);
    }

    _manualLoadFromHash(hashValues, modelData) {
        if (hashValues.showAdcVariants)
            modelData.showAdcVariants = this._parseObjectHashParam(hashValues.showAdcVariants, { isBoolean: true });
    }

    _parseObjectHashParam(hashValue, config = {}) {
        let result = {};
        hashValue.split(';').forEach((item) => {
            let keyValuePair = item.split(':');
            if (keyValuePair.length === 2) {
                if (config.isNumeric) result[keyValuePair[0]] = Number(keyValuePair[1]);
                else if (config.isBoolean) result[keyValuePair[0]] = keyValuePair[1] === 'true';
                else result[keyValuePair[0]] = keyValuePair[1];
            }
        });

        return result;
    }

    _handleHashItem(hashKey, metadata, modelData) {
        let hashValue = this.hashService.parsedLocationHash[hashKey],
            /**
             * We need to find the metadata where hashKey is matched
             * or there is a match to the appVarName (the hash key does not match the model var name)
             */
            foundMetadata = metadata[hashKey];

        if (foundMetadata) {
            this._validateAndSetHashItem(hashValue, foundMetadata, modelData);
        }
    }

    _validateAndSetHashItem(hashValue, metadata, modelData) {
        if (metadata.isNumeric) {
            if (typeof hashValue !== 'undefined' && Utils.isNumeric(hashValue)) {
                modelData[metadata.appVarName] = Number(hashValue);
            }
        } else if (metadata.isBoolean) {
            if (typeof hashValue !== 'undefined') {
                if (hashValue === 'true') modelData[metadata.appVarName] = true;
                else if (hashValue === 'false') modelData[metadata.appVarName] = false;
            }
            //modelData[metadata.appVarName] = typeof hashValue !== 'undefined' && hashValue === "true";
        } else {
            modelData[metadata.appVarName] = hashValue;
        }
    }

    setUrlParameter(parameterName, remove = false) {
        let foundMetadata = this.initial.getHashMetadataItem(parameterName.split('.')[1]);
        if (foundMetadata) {
            this.hashService.setUrlParameter(foundMetadata.hashKey, remove ? null : this.get(parameterName));
        }
    }

    setUrlParameterForObject(parameterName) {
        let objectValue = this.get(`model.${parameterName}`);
        let hashValue = Object.keys(objectValue)
            .map((key) => `${key}:${objectValue[key]}`)
            .join(';');
        this.hashService.setUrlParameter(parameterName, hashValue);
    }

    getDefaultShowVariantForType(type) {
        return this.getDefaultForParameter(`show${upperFirst(type)}Variant`);
    }

    getDefaultForParameter(parameterName) {
        let metadata = this.initial.getHashMetadataItem(parameterName);
        return metadata ? metadata.default : null;
    }

    setOptionalSourceSettingsProperties(initialLoad = false) {
        this.setAdditionalProperties('model.showOptionalSourceSettings', ['wbNoise', 'thd'], initialLoad);
    }

    setAdditionalProperties(originalPropertyName, additionalPropertyNames, initialLoad = false) {
        let initial = this.initial,
            // model.showOptionalSourceSettings
            ossMetadata = initial.getHashMetadataItem(originalPropertyName.split('.')[1]),
            // ['wbNoise', 'thd']
            metadata = initial.getHashMetadata(additionalPropertyNames),
            modelData = initial.metadataToModelData(metadata),
            hashValues = this.hashService.parsedLocationHash;

        // 'model.showOptionalSourceSettings'
        if (this.get(originalPropertyName)) {
            if (initialLoad)
                Object.keys(hashValues).forEach((hashKey) => this._handleHashItem(hashKey, metadata, modelData));
            this.model.setProperties(modelData);
        } else {
            this.model.setProperties(ossMetadata.addlDefaults);
        }
    }

    setUrlParameterWithSelectedPart(parameterName) {
        let modelName = `model.selected${upperFirst(parameterName)}.part`;
        this.hashService.setUrlParameter(parameterName, this.get(modelName));

        this.analytics.setWebEvent(this.partData.toolName, `${parameterName}Selected`, this.get(modelName));
    }

    setUrlParameterWithSelectedId(parameterName) {
        this.hashService.setUrlParameter(parameterName, this.get(`model.selected${upperFirst(parameterName)}.id`));
    }

    setUrlParameterWithId(sourceName, parameterName) {
        this.hashService.setUrlParameter(parameterName, this.get(`${sourceName}.${parameterName}.id`));
    }
}
